import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import '@kitware/vtk.js/Rendering/Profiles/Geometry';
import vtkFullScreenRenderWindow from '@kitware/vtk.js/Rendering/Misc/FullScreenRenderWindow';
import vtkSTLReader from '@kitware/vtk.js/IO/Geometry/STLReader';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkCylinderSource from '@kitware/vtk.js/Filters/Sources/CylinderSource';
import vtkCubeSource from '@kitware/vtk.js/Filters/Sources/CubeSource';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import vtkTriangleFilter from '@kitware/vtk.js/Filters/General/TriangleFilter';
import vtkInteractorStyleTrackballCamera from '@kitware/vtk.js/Interaction/Style/InteractorStyleTrackballCamera';
import vtkLight from '@kitware/vtk.js/Rendering/Core/Light';
import vtkMatrixBuilder from '@kitware/vtk.js/Common/Core/MatrixBuilder';
import { getVertebraColor, vertebraToLabelMap } from '../styles/colorUtils';
import API_URL from '../config/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('armita_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// VTK Background color
const BACKGROUND_COLOR = [0.16, 0.16, 0.16]; // #2a2a2a in RGB

// Helper function to convert hex to RGB for VTK
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16) / 255,
    parseInt(result[2], 16) / 255,
    parseInt(result[3], 16) / 255
  ] : [1, 1, 1];
};

// Centralized function to get material properties based on state
const getMaterialProperties = (isSelected, isConcerning, isHerniated) => {
  let color, ambient, diffuse, specular, specularPower, opacity, ambientColor;

  if (isSelected && (isConcerning || isHerniated)) {
    // GREEN for vertebrae that are both selected AND (concerning OR herniated)
    color = hexToRgb('#4caf50'); // Material Design Green
    ambientColor = [color[0] * 1.5, color[1] * 1.5, color[2] * 1.5];
    ambient = 0.4;
    diffuse = 0.9;
    specular = 0.6;
    specularPower = 40;
    opacity = 1.0;
  } else if (isConcerning) {
    // Red for concerning (Stenosis/Critical)
    color = hexToRgb('#ff6b6b');
    ambientColor = [color[0] * 1.3, color[1] * 1.3, color[2] * 1.3];
    ambient = 0.35;
    diffuse = 0.85;
    specular = 0.6;
    specularPower = 35;
    opacity = 1.0;
  } else if (isHerniated) {
    // Orange for Herniation (Warning)
    color = hexToRgb('#ff8f00'); // Material Design Orange
    ambientColor = [color[0] * 1.3, color[1] * 1.3, color[2] * 1.3];
    ambient = 0.35;
    diffuse = 0.85;
    specular = 0.5;
    specularPower = 30;
    opacity = 1.0;
  } else if (isSelected) {
    // Blue for selected only
    color = hexToRgb('#3f88c5');
    ambient = 0.3;
    diffuse = 0.8;
    specular = 0.5;
    specularPower = 30;
    opacity = 1.0;
    ambientColor = [color[0] * 1.4, color[1] * 1.4, color[2] * 1.4];
  } else {
    // Normal bone color
    color = hexToRgb('#e3dac9');
    ambient = 0.25;
    diffuse = 0.7;
    specular = 0.2;
    specularPower = 10;
    opacity = 1.0;
    ambientColor = [0.4, 0.4, 0.4];
  }

  return { color, ambient, diffuse, specular, specularPower, opacity, ambientColor };
};

// Loading component
const LoadingIndicator = ({ progress }) => {
  return (
    <div
      className="glass"
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 6,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-5)',
        width: 'min(260px, calc(100% - 32px))',
        textAlign: 'center'
      }}
    >
      <div className="spinner" />
      <div style={{
        fontSize: '0.86rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        color: 'var(--text-primary)'
      }}>
        Loading spine model
      </div>
      <div className="progress">
        <div className="progress__fill" style={{ width: `${progress}%` }} />
      </div>
      <div style={{
        fontSize: '0.72rem',
        fontFamily: 'var(--font-mono)',
        color: 'var(--text-tertiary)'
      }}>
        {Math.round(progress)}%
      </div>
    </div>
  );
};

// Error component
const ErrorDisplay = ({ error }) => {
  return (
    <div
      className="glass"
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 6,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-5)',
        width: 'min(320px, calc(100% - 32px))',
        textAlign: 'center'
      }}
    >
      <div style={{
        fontSize: '0.92rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        color: 'var(--danger)'
      }}>
        Error loading spine model
      </div>
      <div style={{
        fontSize: '0.78rem',
        color: 'var(--text-secondary)',
        overflowWrap: 'anywhere'
      }}>
        {error}
      </div>
    </div>
  );
};

// Individual vertebra component using VTK (memoized to prevent unnecessary re-renders)
const VTKVertebra = React.memo(({
  modelUrl,
  isSelected,
  isConcerning,
  isHerniated,
  isVisible,
  vertebraLabel,
  renderer,
  userMatrix,
  onBoundsReady,
  onLoaded
}) => {
  const actorRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Log removed due to undefined variables
    console.log(`[VTKVertebra] Main effect triggered for ${vertebraLabel}: visible=${isVisible}, renderer=${!!renderer}, hasActor=${!!actorRef.current}`);
    if (!renderer) {
      console.log(`[VTKVertebra] Skipping load for ${vertebraLabel}: renderer not available`);
      return;
    }

    // Apply matrix if actor exists
    if (actorRef.current && userMatrix) {
      actorRef.current.setUserMatrix(userMatrix);
      if (renderer.getRenderWindow) {
        renderer.getRenderWindow().render();
      }
    }

    // If actor already exists, just update visibility and return
    if (actorRef.current) {
      console.log(`[VTKVertebra] Actor already exists for ${vertebraLabel}, updating visibility`);
      actorRef.current.setVisibility(isVisible);
      if (isVisible && renderer.getRenderWindow) {
        renderer.getRenderWindow().render();
      }
      return;
    }

    // Only load if visible
    if (!isVisible) {
      console.log(`[VTKVertebra] Skipping load for invisible ${vertebraLabel}`);
      return;
    }

    let isMounted = true;

    const loadSTL = async () => {
      try {
        console.log(`[VTKVertebra] Loading STL for ${vertebraLabel} from ${modelUrl}`);
        setLoading(true);
        setError(false);

        // Create STL reader
        const reader = vtkSTLReader.newInstance();

        // Fetch STL data
        const response = await fetch(modelUrl, { headers: getAuthHeaders() });
        if (!response.ok) {
          throw new Error(`Failed to load STL: ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        console.log(`Loaded ${arrayBuffer.byteLength} bytes for ${vertebraLabel}`);
        reader.parseAsArrayBuffer(arrayBuffer);

        if (!isMounted) return;

        // Create triangle filter for better mesh quality (provides some smoothing effect)
        const triangleFilter = vtkTriangleFilter.newInstance();
        triangleFilter.setInputConnection(reader.getOutputPort());

        // Create mapper
        const mapper = vtkMapper.newInstance();
        mapper.setInputConnection(triangleFilter.getOutputPort());

        // Create actor
        const actor = vtkActor.newInstance();
        actor.setMapper(mapper);

        // Set color and material properties using centralized function
        const materialProps = getMaterialProperties(isSelected, isConcerning, isHerniated);
        const { color, ambient, diffuse, specular, specularPower, opacity, ambientColor } = materialProps;

        // Apply enhanced material properties
        const property = actor.getProperty();
        property.setColor(color[0], color[1], color[2]);
        property.setAmbient(ambient);
        property.setDiffuse(diffuse);
        property.setSpecular(specular);
        property.setSpecularPower(specularPower);
        property.setOpacity(opacity);

        // Apply ambient color (includes special handling for selected+concerning)
        property.setAmbientColor(ambientColor[0], ambientColor[1], ambientColor[2]);

        // Add to renderer
        renderer.addActor(actor);
        actorRef.current = actor;

        // Force pipeline update to ensure bounds are calculated
        if (mapper.update) mapper.update();

        // Robust bounds validation
        const getValidBounds = () => {
          const isValid = (b) => {
            if (!b || b.length !== 6) return false;
            // Check for Infinity or Max Value (uninitialized VTK bounds are usually +/- 1e30 or similar)
            // We use 1e10 as a safe clamp for medical scale (usually mm)
            for (let i = 0; i < 6; i++) {
              if (!Number.isFinite(b[i]) || Math.abs(b[i]) > 1e10) return false;
            }
            // Check for inverted bounds (min > max) which means invalid/empty
            if (b[0] > b[1] || b[2] > b[3] || b[4] > b[5]) return false;

            return true;
          };

          // 1. Try Source Reader bounds (raw geometry) - THIS IS MOST RELIABLE for static STL
          // Actor/Mapper bounds might rely on the rendering pipeline being up to date.
          if (reader.getOutputData && reader.getOutputData()) {
            const data = reader.getOutputData();
            if (data.getBounds) {
              const b = data.getBounds();
              if (isValid(b)) return b;
            }
          }

          // 2. Try Mapper bounds
          if (mapper.getBounds) {
            const b = mapper.getBounds();
            if (isValid(b)) return b;
          }

          // 3. Try Actor bounds (last resort, might be untransformed or uninitialized)
          const b = actor.getBounds();
          if (isValid(b)) return b;

          return null;
        };

        const bounds = getValidBounds();

        // Only report if we have valid bounds
        if (bounds) {
          console.log(`[VTKVertebra] ${vertebraLabel} bounds:`, bounds);
          if (onBoundsReady) {
            onBoundsReady(vertebraLabel, bounds);
          }
        } else {
          console.warn(`[VTKVertebra] Could not determine valid bounds for ${vertebraLabel}`);
        }

        setLoading(false);
        console.log(`[VTKVertebra] Successfully loaded and added ${vertebraLabel} to renderer`);
        if (onLoaded) onLoaded(vertebraLabel);

        // Trigger a render (batched)
        if (renderer.getRenderWindow) {
          requestAnimationFrame(() => {
            renderer.getRenderWindow().render();
          });
        }

      } catch (err) {
        if (isMounted) {
          console.error(`Error loading vertebra ${vertebraLabel}:`, err);
          setError(true);
          setLoading(false);
        }
      }
    };

    loadSTL();

    return () => {
      isMounted = false;
      if (actorRef.current && renderer) {
        renderer.removeActor(actorRef.current);
        actorRef.current = null;
      }
    };
  }, [modelUrl, vertebraLabel, renderer, onLoaded, isVisible, isHerniated]); // Keep isVisible but handle it properly

  // Update actor color and materials when selection state changes (debounced)
  const materialUpdateTimeoutRef = useRef(null);
  useEffect(() => {
    // Log removed due to undefined variables
    if (actorRef.current) {
      // Clear any pending material update
      if (materialUpdateTimeoutRef.current) {
        clearTimeout(materialUpdateTimeoutRef.current);
      }

      // Debounce material updates to prevent flickering during rapid state changes
      materialUpdateTimeoutRef.current = setTimeout(() => {
        if (!actorRef.current) return; // Check if actor still exists

        console.log(`[VTKVertebra] Updating ${vertebraLabel} state: selected=${isSelected}, concerning=${isConcerning}, herniated=${isHerniated}${isSelected && isConcerning ? ' (GREEN - both selected and concerning)' : ''}`);

        const materialProps = getMaterialProperties(isSelected, isConcerning, isHerniated);
        const { color, ambient, diffuse, specular, specularPower, opacity, ambientColor } = materialProps;

        // Apply enhanced material properties
        const property = actorRef.current.getProperty();
        property.setColor(color[0], color[1], color[2]);
        property.setAmbient(ambient);
        property.setDiffuse(diffuse);
        property.setSpecular(specular);
        property.setSpecularPower(specularPower);
        property.setOpacity(opacity);

        // Apply ambient color (includes special handling for selected+concerning)
        property.setAmbientColor(ambientColor[0], ambientColor[1], ambientColor[2]);

        // Mark for re-render (will be batched)
        if (renderer && renderer.getRenderWindow) {
          // Use requestAnimationFrame to batch renders
          requestAnimationFrame(() => {
            if (renderer.getRenderWindow) {
              renderer.getRenderWindow().render();
            }
          });
        }
      }, 16); // ~60fps debounce
    }

    return () => {
      if (materialUpdateTimeoutRef.current) {
        clearTimeout(materialUpdateTimeoutRef.current);
      }
    };
  }, [isSelected, isConcerning, isHerniated, vertebraLabel, renderer]);

  // Update user matrix separately
  useEffect(() => {
    // Log removed due to undefined variables
    if (actorRef.current && userMatrix) {
      console.log(`[VTKVertebra] Updating matrix for ${vertebraLabel}`);
      actorRef.current.setUserMatrix(userMatrix);
      if (renderer && renderer.getRenderWindow) {
        renderer.getRenderWindow().render();
      }
    }
  }, [userMatrix, renderer, vertebraLabel]);

  // Update visibility only - don't trigger reloads
  useEffect(() => {
    // Log removed due to undefined variables
    if (actorRef.current) {
      console.log(`[VTKVertebra] Setting visibility for ${vertebraLabel}: ${isVisible}`);
      actorRef.current.setVisibility(isVisible);
      // Only render if we're making it visible to avoid flickering
      if (isVisible && renderer && renderer.getRenderWindow) {
        renderer.getRenderWindow().render();
      }
    }
  }, [isVisible, renderer, vertebraLabel]);

  return null; // VTK manages rendering, no React elements needed
}, (prevProps, nextProps) => {
  // Only re-render if the props that affect this specific vertebra have changed
  // This prevents unnecessary re-renders when other vertebrae change
  return (
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isConcerning === nextProps.isConcerning &&
    prevProps.isHerniated === nextProps.isHerniated &&
    prevProps.isVisible === nextProps.isVisible &&
    prevProps.modelUrl === nextProps.modelUrl &&
    prevProps.vertebraLabel === nextProps.vertebraLabel &&
    prevProps.renderer === nextProps.renderer &&
    prevProps.userMatrix === nextProps.userMatrix
  );
});

// Main VTK Scene component
const VTKSpineScene = ({
  selectedLevel,
  selectedLevels = [],
  concerningLevels = [],
  herniatedLevels = [],
  spineOptions,
  corrections, // New prop: { 'L3-L4': 5 }
  distractions, // New prop for vertical height adjustments
  pedicleScrews = {}, // { 'L4': { right: { diameter: 5.5, length: 45, landmarks: {...} }, left: ... } }
  interbodyDevices = {}, // { 'L4-L5': { ...deviceProps, bounds: {...} } }
  vertebralMetrics, // Receive metrics
  showHardware = false, // Only show in Step 2
  viewerId = "unknown"
}) => {
  const containerRef = useRef(null);
  const fullScreenRendererRef = useRef(null);
  const rendererRef = useRef(null);
  const renderWindowRef = useRef(null);
  const hasInitialTransformReset = useRef(false);
  const [spineModels, setSpineModels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState(null);
  const [loadedCount, setLoadedCount] = useState(0);
  const [vtkReady, setVtkReady] = useState(false);
  const [vertebraBounds, setVertebraBounds] = useState({});
  const [calculatedTransforms, setCalculatedTransforms] = useState({});

  // Vertebra lookup (same as Three.js version)
  const vertebraLookup = {
    "C1": 1, "C2": 2, "C3": 3, "C4": 4, "C5": 5, "C6": 6, "C7": 7,
    "T1": 8, "T2": 9, "T3": 10, "T4": 11, "T5": 12, "T6": 13, "T7": 14,
    "T8": 15, "T9": 16, "T10": 17, "T11": 18, "T12": 19,
    "L1": 20, "L2": 21, "L3": 22, "L4": 23, "L5": 24, "S1": 25
  };

  const inverseLookup = Object.entries(vertebraLookup).reduce((acc, [key, value]) => {
    acc[value] = key;
    return acc;
  }, {});

  // Combined selection (same as Three.js version)
  const allSelectedLevels = useMemo(() => {
    const combinedLevels = [...selectedLevels];
    if (selectedLevel && !combinedLevels.includes(selectedLevel)) {
      combinedLevels.push(selectedLevel);
    }
    return combinedLevels;
  }, [selectedLevel, selectedLevels]);

  // Helper to determine vertebra region (same as Three.js version)
  const getVertebraRegion = (level) => {
    const prefix = level.charAt(0);
    switch (prefix) {
      case 'C': return 'cervical';
      case 'T': return 'thoracic';
      case 'L': return 'lumbar';
      case 'S': return 'sacral';
      default: return null;
    }
  };

  // Helper to extract class number (same as Three.js version)
  const getClassNumber = (filename) => {
    try {
      const classStr = filename.split('_class_')[1].split('.')[0];
      return parseInt(classStr);
    } catch (error) {
      console.warn(`Failed to extract class number from filename: ${filename}`, error);
      return 0;
    }
  };

  // Initialize VTK scene (only once)
  // --- Hardware Rendering Logic ---
  const hardwareActors = useRef([]);

  useEffect(() => {
    // Log viewerId to distinguish instances
    console.log(`[DEBUG][${viewerId}] SpineModelViewer Render. Props:`, { showHardware, hasScrews: pedicleScrews && Object.keys(pedicleScrews).length > 0, screwsKeys: pedicleScrews ? Object.keys(pedicleScrews) : "null" });
    // Clear previous hardware actors
    hardwareActors.current.forEach(actor => {
      if (rendererRef.current) rendererRef.current.removeActor(actor);
    });
    hardwareActors.current = [];

    if (!showHardware || !rendererRef.current) {
      return;
    }

    // console.log("Rendering Hardware...", { pedicleScrews, interbodyDevices, vertebraBoundsKeys: Object.keys(vertebraBounds) });

    // 1. Render Pedicle Screws
    console.log(`[DEBUG][${viewerId}] Checking Pedicle Screws Data:`, {
      count: pedicleScrews ? Object.keys(pedicleScrews).length : 'undefined',
      keys: pedicleScrews ? Object.keys(pedicleScrews) : [],
      sample: pedicleScrews && Object.keys(pedicleScrews).length > 0 ? Object.values(pedicleScrews)[0] : 'N/A'
    });
    Object.entries(pedicleScrews).forEach(([level, sides]) => {
      // Apply transformations if exists (from kinematic chain) - currently using global transform if available
      // Note: For now we'll place them in local space of vertebra if we can, or world space
      // Since we don't have easy parent-child actor hierarchy here without assembly, 
      // we'll assume landmarks are in ORIGINAL coordinate space and we need to apply the SAME transform as the vertebra.

      // Debug
      // console.log(`Processing hardware for ${level}`, sides);

      const vertebraMatrix = calculatedTransforms[level] || vtkMatrixBuilder.buildFromDegree().getMatrix(); // Default to identity if not found

      ['right', 'left'].forEach(side => {
        const screwData = sides[side];
        if (!screwData || !screwData.landmarks) return;

        const lm = screwData.landmarks;
        if (!lm.centroid || !lm.major_3D_plus || !lm.major_3D_minus || !lm.minor_3D_plus || !lm.minor_3D_minus) return;

        // Screw dimensions
        const screwRadius = (screwData.diameter || 5.5) / 2;
        const screwLen = screwData.length || 45;

        // Calculate Principal Axes
        // Major Axis (approx Vertical/Z for Lumbar pedicles usually)
        const majorVec = [
          lm.major_3D_plus[0] - lm.major_3D_minus[0],
          lm.major_3D_plus[1] - lm.major_3D_minus[1],
          lm.major_3D_plus[2] - lm.major_3D_minus[2]
        ];

        // Minor Axis (approx Horizontal/X)
        const minorVec = [
          lm.minor_3D_plus[0] - lm.minor_3D_minus[0],
          lm.minor_3D_plus[1] - lm.minor_3D_minus[1],
          lm.minor_3D_plus[2] - lm.minor_3D_minus[2]
        ];

        // Pedicle Axis (Trajectory) is Perpendicular to Cross-Section (Major x Minor)
        // Vector = Cross(Major, Minor)
        // Note: Ordering determines Forward vs Backward. We Check against Centroid-to-Body later.
        let vector = [
          majorVec[1] * minorVec[2] - majorVec[2] * minorVec[1],
          majorVec[2] * minorVec[0] - majorVec[0] * minorVec[2],
          majorVec[0] * minorVec[1] - majorVec[1] * minorVec[0]
        ];

        // Normalize direction
        const vecLen = Math.sqrt(vector[0] * vector[0] + vector[1] * vector[1] + vector[2] * vector[2]);
        const direction = [vector[0] / vecLen, vector[1] / vecLen, vector[2] / vecLen];

        // Determine Direction Sign (should point Anteriorly)
        // Heuristic: Centroid of Vertebra (from bounds) is roughly Anterior to Pedicle.
        const vertBounds = vertebraBounds[level];
        if (vertBounds) {
          const vertCenter = [
            (vertBounds[0] + vertBounds[1]) / 2,
            (vertBounds[2] + vertBounds[3]) / 2,
            (vertBounds[4] + vertBounds[5]) / 2
          ];
          const toCenter = [
            vertCenter[0] - lm.centroid[0],
            vertCenter[1] - lm.centroid[1],
            vertCenter[2] - lm.centroid[2]
          ];
          // Dot Product
          const dot = direction[0] * toCenter[0] + direction[1] * toCenter[1] + direction[2] * toCenter[2];
          if (dot < 0) {
            // Flip direction
            direction[0] = -direction[0];
            direction[1] = -direction[1];
            direction[2] = -direction[2];
          }
        }


        // Create Cylinder
        const cylinderSource = vtkCylinderSource.newInstance({
          height: screwLen,
          radius: screwRadius,
          resolution: 20
        });

        const mapper = vtkMapper.newInstance();
        mapper.setInputConnection(cylinderSource.getOutputPort());

        const actor = vtkActor.newInstance();
        actor.setMapper(mapper);

        // Material - Titanium look
        const prop = actor.getProperty();
        prop.setColor(0.7, 0.7, 0.8); // Silver/Grey
        prop.setMetallic(1.0);
        prop.setSpecular(0.8);
        prop.setSpecularPower(80);

        // Position and Rotate
        // 1. Orient Cylinder (default Y-axis) to 'direction'
        // vtkCylinderSource default axis is Y=[0,1,0].
        const axisY = [0, 1, 0];

        // Rotation Axis = Cross(Y, Direction)
        const rotAxis = [
          axisY[1] * direction[2] - axisY[2] * direction[1],
          axisY[2] * direction[0] - axisY[0] * direction[2],
          axisY[0] * direction[1] - axisY[1] * direction[0]
        ];

        // Angle = acos(Dot(Y, Direction))
        const dotY = axisY[0] * direction[0] + axisY[1] * direction[1] + axisY[2] * direction[2];
        // Clamp for safety
        const clampedDot = Math.max(-1, Math.min(1, dotY));
        const angleRad = Math.acos(clampedDot);
        const angleDeg = angleRad * (180 / Math.PI);


        // 2. Position
        // Use lm.centroid as the CENTER of the cylinder.
        // (Assuming centroid is the isthmus center, screw goes through it).
        const centerPos = lm.centroid;

        const finalMatrix = vtkMatrixBuilder.buildFromDegree();

        // Apply Vertebra Transform (Kinematics)
        finalMatrix.setMatrix(vertebraMatrix);

        // Transform to World Position + Orientation
        finalMatrix.translate(centerPos[0], centerPos[1], centerPos[2]);
        if (Math.abs(angleDeg) > 0.01) {
          finalMatrix.rotate(angleDeg, rotAxis);
        }

        actor.setUserMatrix(finalMatrix.getMatrix());

        rendererRef.current.addActor(actor);
        hardwareActors.current.push(actor);
      });
    });

    // 2. Render Interbody Devices
    Object.entries(interbodyDevices).forEach(([level, device]) => {
      // device: { ...data, bounds: { upper: [...], lower: [...] } }
      // We need bounds to position it.
      // Wait, the parent component needs to pass us the *calculated* bounds or position?
      // Or we calculate midpoint of relevant vertebrae here.

      // Parse level "L4-L5"
      const parts = level.split('-');
      if (parts.length !== 2) return;
      const upperLevel = parts[0];
      const lowerLevel = parts[1];

      const upperBounds = vertebraBounds[upperLevel];
      const lowerBounds = vertebraBounds[lowerLevel];

      console.log(`[DEBUG] Device ${level}: Upper=${upperLevel} Bounds=${!!upperBounds}, Lower=${lowerLevel} Bounds=${!!lowerBounds}`);
      if (upperBounds) console.log(`[DEBUG] Upper Bounds:`, upperBounds);
      if (lowerBounds) console.log(`[DEBUG] Lower Bounds:`, lowerBounds);


      // Check for valid, finite bounds
      const isValidBounds = (b) => b && b.length === 6 && b.every(val => Number.isFinite(val) && Math.abs(val) < 1e10);

      if (!isValidBounds(upperBounds) || !isValidBounds(lowerBounds)) {
        console.warn(`[SpineModelViewer] Invalid/Pending bounds for device at ${level}. Upper(${upperLevel}): ${JSON.stringify(upperBounds)}, Lower(${lowerLevel}): ${JSON.stringify(lowerBounds)}`);
        return;
      }

      // Calculate disc center (midpoint between inferior of upper and superior of lower)
      // Roughly: (Upper.minZ + Lower.maxZ) / 2 ? No, Z is usually up/down but in medical coords it varies.
      // Let's use simple bounds center average for now.

      // Calculate disc center targeted at Anterior region
      // Bounds: [xmin, xmax, ymin, ymax, zmin, zmax]
      // P = (min+max)/2 is geometric center (often inside spinal canal/posterior arch for whole-vertebra STLs)
      // Vertebral Body is Anterior.
      // Assuming Y is AP axis. 
      // We need to shift ANTERIORLY from geometric center.
      // If +Y is Posterior, we shift -Y. If +Y is Anterior, we shift +Y.
      // Medical images (RAS): +Y is Anterior? No, LAS/LPS.
      // Let's rely on bounds:
      // If we assume the vertebral body is the "thick" part anteriorly.
      // Usually, Y_posterior > Y_anterior (LPS) or Y_anterior > Y_posterior (RAS).

      // Heuristic: Vertebral Body center is roughly at 25-35% of the total AP depth from the Anterior face.
      // If we don't know polarity, we can check where the "Canal" is.
      // Canal Centroid from logic above (not accessible here easily without parsing).
      // Let's assume standard VTK view: Camera at +Y looking -Y?
      // Usually +Z Up, +Y Posterior (Screen Surface), +X Right.
      // So Anterior is -Y.

      const getAnteriorQuarter = (min, max, levelMetrics) => {
        // If we have precise vertebral depth, use it
        if (levelMetrics && levelMetrics.vertebralDepth) {
          const depth = parseFloat(levelMetrics.vertebralDepth);
          // Assuming Anterior is at Min Y (Standard VTK/Medical orientation often has Y axis pointing Posteriorly? No, it depends).
          // Heuristic check:
          // If we assume standard anterior-approach view, Min Y is closer to screen (if looking from front) or Max Y?
          // Let's stick to the assumption that Body is at the "start" of the Y range (lowest index/coordinate usually if normalized).
          // OR, relying on the previous specific logic: `min + (max-min)*0.35`.
          // If we have depth: `min + depth/2`.
          // Let's verify if `depth` makes sense relative to bounds.
          const boundsLen = max - min;
          if (depth > 0 && depth < boundsLen) {
            const calculated = min + (depth / 2);
            console.log(`[DEBUG] Using precise depth for ${level}: ${depth}. BoundsLen: ${boundsLen}. Center: ${calculated}`);
            return calculated;
          }
        }
        return min + (max - min) * 0.35;
      };

      // 1. Determine Distraction for this level
      const pairKey = `${upperLevel}-${lowerLevel}`;
      const distractionMm = distractions && distractions[pairKey] ? parseFloat(distractions[pairKey]) : 0;

      // 2. Flip Y-Axis Assumption: Max Y is Anterior (RAS) or simply opposite of previous.
      // Previous: Min + 0.25 * Range (assuming Min was Anterior)
      // Feedback: "Intersecting geometries" (overlapping pedicles). Pedicles are Posterior.
      // If we were at Min and hitting pedicles, then Min is Posterior.
      // So Max must be Anterior.
      // Target: Max - 0.25 * Range.

      const upperCenter = [
        (upperBounds[0] + upperBounds[1]) / 2, // X Center
        upperBounds[3] - (upperBounds[3] - upperBounds[2]) * 0.25, // Target Max Y (Anterior?)
        (upperBounds[4] + upperBounds[5]) / 2  // Z Center
      ];

      const lowerCenter = [
        (lowerBounds[0] + lowerBounds[1]) / 2,
        lowerBounds[3] - (lowerBounds[3] - lowerBounds[2]) * 0.25,
        (lowerBounds[4] + lowerBounds[5]) / 2
      ];

      // Refine Z to target Disc Space Interface:
      // Upper Vertebra (Superior) -> Bottom Surface is MinZ (bounds[4])
      // Lower Vertebra (Inferior) -> Top Surface is MaxZ (bounds[5])
      // Add half of distraction to center it in the new gap
      const discZ = ((upperBounds[4] + lowerBounds[5]) / 2) + (distractionMm / 2);

      const discCenter = [
        (upperCenter[0] + lowerCenter[0]) / 2,
        (upperCenter[1] + lowerCenter[1]) / 2,
        discZ
      ];
      console.log(`[DEBUG] Disc Center ${level} (Corrected Y-Flip + Distraction):`, discCenter);

      // Device Dimensions
      // Casting helper for keys like "10", "10-12" (take max)
      const parseDim = (val) => {
        if (!val) return 0;
        const strVal = String(val);
        if (strVal.includes('-')) {
          const parts = strVal.split('-').map(p => parseFloat(p));
          const validParts = parts.filter(p => !isNaN(p) && p > 0);
          if (validParts.length > 0) return Math.max(...validParts);
        }
        return parseFloat(strVal);
      };

      // Use exact dimensions from the device selection
      const xWidth = parseDim(device.width || device.Width || device['Device Width']);
      const yDepth = parseDim(device.depth || device.Depth || device.length || device['Device Depth']);
      const zHeight = parseDim(device.height || device.Height || device['Anterior Height'] || device['Posterior Height']);

      // If any dimension is missing/invalid, do not render a fallback box that is misleading
      if (!xWidth || !yDepth || !zHeight) {
        console.warn(`[SpineModelViewer] Invalid device dimensions for ${level}:`, {
          raw: device,
          parsed: { xWidth, yDepth, zHeight }
        });
        return;
      }
      console.log(`[DEBUG] Device Dims ${level}:`, { xWidth, yDepth, zHeight });

      const cubeSource = vtkCubeSource.newInstance({
        xLength: xWidth,
        yLength: yDepth,
        zLength: zHeight
      });

      const mapper = vtkMapper.newInstance();
      mapper.setInputConnection(cubeSource.getOutputPort());

      const actor = vtkActor.newInstance();
      actor.setMapper(mapper);

      const prop = actor.getProperty();
      prop.setColor(0.2, 0.6, 1.0); // Blue-ish
      prop.setOpacity(0.8);

      // Position
      const mat = vtkMatrixBuilder.buildFromDegree();

      // We want to place the device at 'discCenter' (World Coordinates).
      // We want it to inherit the ROTATION of the lower vertebra, but not its translation (since we have its own position).

      const transformMatrix = calculatedTransforms[lowerLevel];

      // 2. Then apply rotation from vertebra including local offset

      // 2. Then apply rotation from vertebra
      if (transformMatrix) {
        // vtkMatrixBuilder operations are cumulative.
        // Final = M_vertebra * T_translation(discCenter).
        // This applies the vertebra's rotation/position to the device's original position (discCenter)
        // effectively pinning it to the vertebra's coordinate space.

        mat.setMatrix(transformMatrix); // Apply kinematic transform M
        mat.translate(discCenter[0], discCenter[1], discCenter[2]); // Apply original position
      } else {
        // No transform, just translate to world pos
        mat.identity().translate(discCenter[0], discCenter[1], discCenter[2]);
      }

      actor.setUserMatrix(mat.getMatrix());

      rendererRef.current.addActor(actor);
      hardwareActors.current.push(actor);
    });

    if (rendererRef.current && rendererRef.current.getRenderWindow()) {
      rendererRef.current.getRenderWindow().render();
    }

  }, [pedicleScrews, interbodyDevices, vertebralMetrics, showHardware, calculatedTransforms, vertebraBounds, distractions]);

  useEffect(() => {
    console.log("[DEBUG] SpineModelViewer Render. Props:", { showHardware, hasScrews: pedicleScrews && Object.keys(pedicleScrews).length > 0, screwsKeys: pedicleScrews ? Object.keys(pedicleScrews) : "null" });
    console.log('VTK initialization effect triggered');

    const container = containerRef.current;
    if (!container || vtkReady) {
      console.log('Container not available or VTK already ready');
      return;
    }

    console.log('Container dimensions:', container.clientWidth, 'x', container.clientHeight);

    // Force container to have dimensions if it doesn't. No fixed min-height:
    // a 400px floor overflowed shorter host panels (Planning's alignment
    // cards) and the clipped canvas read as an over-zoomed model.
    if (container.clientWidth === 0 || container.clientHeight === 0) {
      console.log('Setting container dimensions');
      container.style.width = '100%';
      container.style.height = '100%';
    }

    let disposed = false;

    const initializeVTK = () => {
      if (disposed) return;
      try {
        console.log('Starting VTK initialization...');

        // Create full screen render window with proper initialization
        const fullScreenRenderer = vtkFullScreenRenderWindow.newInstance({
          rootContainer: container,
          background: BACKGROUND_COLOR,
        });

        console.log('FullScreenRenderer created:', !!fullScreenRenderer);

        // Get components immediately after creation
        const renderer = fullScreenRenderer.getRenderer();
        const renderWindow = fullScreenRenderer.getRenderWindow();

        console.log('Renderer:', !!renderer, 'RenderWindow:', !!renderWindow);

        if (!renderer || !renderWindow) {
          console.error('Failed to initialize VTK renderer or render window');
          setError('Failed to initialize 3D viewer');
          return;
        }

        // Set up enhanced lighting to match Three.js quality
        // Ambient light equivalent
        renderer.setTwoSidedLighting(false);

        // Main spotlight
        const spotLight = vtkLight.newInstance();
        spotLight.setLightType('SceneLight');
        spotLight.setPosition(100, 100, 100);
        spotLight.setIntensity(1.0);
        spotLight.setColor(1, 1, 1);
        renderer.addLight(spotLight);

        // Directional light  
        const dirLight = vtkLight.newInstance();
        dirLight.setLightType('SceneLight');
        dirLight.setPosition(-50, -50, 50);
        dirLight.setIntensity(0.8);
        dirLight.setColor(1, 1, 1);
        renderer.addLight(dirLight);

        // Point lights for better coverage
        const pointLight1 = vtkLight.newInstance();
        pointLight1.setLightType('SceneLight');
        pointLight1.setPosition(-10, -10, -10);
        pointLight1.setIntensity(0.6);
        renderer.addLight(pointLight1);

        const topLight = vtkLight.newInstance();
        topLight.setLightType('SceneLight');
        topLight.setPosition(0, 200, 0);
        topLight.setIntensity(0.5);
        renderer.addLight(topLight);

        const bottomLight = vtkLight.newInstance();
        bottomLight.setLightType('SceneLight');
        bottomLight.setPosition(0, -200, 0);
        bottomLight.setIntensity(0.3);
        renderer.addLight(bottomLight);

        // Set up camera (similar position to Three.js version)
        const camera = renderer.getActiveCamera();
        camera.setPosition(400, 0, 100);
        camera.setFocalPoint(0, 0, 0);
        camera.setViewUp(0, 0, 1);

        // Set up interaction
        const interactorStyle = vtkInteractorStyleTrackballCamera.newInstance();
        const interactor = renderWindow.getInteractor();
        if (interactor) {
          interactor.setInteractorStyle(interactorStyle);
        }

        // Store references
        fullScreenRendererRef.current = fullScreenRenderer;
        rendererRef.current = renderer;
        renderWindowRef.current = renderWindow;

        console.log('VTK scene initialized successfully');

        // Mark VTK as ready
        setVtkReady(true);

        // Force initial render
        renderWindow.render();

        console.log('VTK initial render completed, camera position:', camera.getPosition());

      } catch (error) {
        console.error('Error initializing VTK scene:', error);
        setError(`Failed to initialize 3D viewer: ${error.message}`);
      }
    };

    // Start initialization immediately
    const initTimeoutId = setTimeout(initializeVTK, 100);

    return () => {
      console.log('VTK useEffect cleanup triggered');
      disposed = true;
      clearTimeout(initTimeoutId);
    };
  }, []);

  // Cleanup VTK on component unmount
  useEffect(() => {
    console.log("[DEBUG] SpineModelViewer Render. Props:", { showHardware, hasScrews: pedicleScrews && Object.keys(pedicleScrews).length > 0, screwsKeys: pedicleScrews ? Object.keys(pedicleScrews) : "null" });
    return () => {
      console.log('Component unmounting, cleaning up VTK scene');

      // Clean up renderer and render window
      if (renderWindowRef.current) {
        try {
          // Remove all actors first
          if (rendererRef.current) {
            rendererRef.current.removeAllActors();
          }

          // Disable interactor to stop resize observations
          const interactor = renderWindowRef.current.getInteractor();
          if (interactor) {
            interactor.disable();
          }

          renderWindowRef.current = null;
        } catch (e) {
          console.warn('Error cleaning up render window:', e);
        }
      }

      if (fullScreenRendererRef.current) {
        try {
          // This should handle removing resize observers
          fullScreenRendererRef.current.setContainer(null);
          fullScreenRendererRef.current.delete();
        } catch (e) {
          console.warn('Error cleaning up VTK:', e);
        }
      }

      // Clear refs
      rendererRef.current = null;
      fullScreenRendererRef.current = null;
    };
  }, []);

  // Load spine models data (only once when renderer is ready)
  useEffect(() => {
    console.log("[DEBUG] SpineModelViewer Render. Props:", { showHardware, hasScrews: pedicleScrews && Object.keys(pedicleScrews).length > 0, screwsKeys: pedicleScrews ? Object.keys(pedicleScrews) : "null" });
    // Wait for VTK renderer to be initialized
    if (!vtkReady || !rendererRef.current) {
      console.log("Waiting for VTK renderer to initialize...");
      return;
    }

    // Only fetch if we don't have models yet
    if (spineModels.length > 0) {
      console.log("Models already loaded, skipping fetch");
      return;
    }

    console.log("VTK renderer is ready, starting STL loading...");

    const fetchSTLFiles = async () => {
      try {
        setIsLoading(true);
        setLoadingProgress(0);
        setError(null);
        setLoadedCount(0);

        console.log("Fetching STL files from:", `${API_URL}/spine-models`);

        const response = await fetch(`${API_URL}/spine-models`, { headers: getAuthHeaders() });
        if (!response.ok) {
          throw new Error(`Failed to fetch spine models list: ${response.status}`);
        }

        const modelsData = await response.json();
        console.log("Received STL files data:", modelsData);

        // Process model data (same logic as Three.js version)
        const sortedModels = [...modelsData.models].sort((a, b) => {
          const classNumA = getClassNumber(a.filename);
          const classNumB = getClassNumber(b.filename);
          return classNumA - classNumB;
        });

        const processedModels = sortedModels.map((model, index) => {
          const classNumber = getClassNumber(model.filename);
          let level = inverseLookup[classNumber] || `Unknown_${classNumber}`;

          // Fallback level detection (same as Three.js version)
          if (level.startsWith('Unknown_')) {
            const levelMatch = model.filename.match(/_([CTLS][0-9]+)_/i);
            if (levelMatch) {
              level = levelMatch[1].toUpperCase();
            }
          }

          if (level.startsWith('Unknown_')) {
            if (classNumber >= 1 && classNumber <= 7) {
              level = `C${classNumber}`;
            } else if (classNumber >= 8 && classNumber <= 19) {
              level = `T${classNumber - 7}`;
            } else if (classNumber >= 20 && classNumber <= 24) {
              level = `L${classNumber - 19}`;
            } else if (classNumber === 25) {
              level = 'S1';
            }
          }

          return {
            url: model.url || `${API_URL}/spine-models/${model.filename}`,
            filename: model.filename,
            level,
            classNumber,
            isSelected: false, // Will be updated in separate effect
            isConcerning: false, // Will be updated in separate effect  
            isVisible: true // Will be updated in separate effect
          };
        });

        setSpineModels(processedModels);
        console.log(`Processed ${processedModels.length} models`);

        // If no models to load, complete loading immediately
        if (processedModels.length === 0) {
          console.log("No models to load, completing loading");
          setIsLoading(false);
          setLoadingProgress(100);
        }

      } catch (error) {
        console.error('Error loading spine models:', error);
        setError(error.message);
        setIsLoading(false);
      }
    };

    fetchSTLFiles();
  }, [vtkReady]);

  // Create computed models with current selection and visibility states
  const computedModels = useMemo(() => {
    if (spineModels.length === 0) return [];

    // Only log when there are actual changes to selection, concerning, or herniated levels
    const hasSelectedChanges = allSelectedLevels.length > 0;
    const hasConcerningChanges = concerningLevels.length > 0;
    const hasHerniatedChanges = herniatedLevels.length > 0;
    if (hasSelectedChanges || hasConcerningChanges || hasHerniatedChanges) {
      console.log("Computing model states - Selected:", allSelectedLevels, "Concerning:", concerningLevels, "Herniated:", herniatedLevels);
    }

    return spineModels.map(model => {
      const region = getVertebraRegion(model.level);
      const isVisible = spineOptions[region] !== false;
      const isSelected = allSelectedLevels.includes(model.level);
      const isConcerning = concerningLevels.includes(model.level);
      const isHerniated = herniatedLevels.includes(model.level);

      return {
        ...model,
        isSelected,
        isConcerning,
        isHerniated,
        isVisible
      };
    });
  }, [spineModels, allSelectedLevels, concerningLevels, herniatedLevels, spineOptions]);

  // Handle bounds ready callback
  const handleBoundsReady = useCallback((level, bounds) => {
    // console.log(`[DEBUG] Bounds ready for ${level}`, bounds);
    setVertebraBounds(prev => ({
      ...prev,
      [level]: bounds
    }));
  }, []);

  // Calculate Kinetic Chain Transforms
  useEffect(() => {
    console.log("[DEBUG] SpineModelViewer Render. Props:", { showHardware, hasScrews: pedicleScrews && Object.keys(pedicleScrews).length > 0, screwsKeys: pedicleScrews ? Object.keys(pedicleScrews) : "null" });
    if (!corrections || Object.keys(corrections).length === 0 || Object.keys(vertebraBounds).length === 0) {
      setCalculatedTransforms({});
      return;
    }

    console.log("Calculating Kinematic Chain Transforms with corrections:", corrections);

    const newTransforms = {};
    // Identity matrix as base
    const identity = vtkMatrixBuilder.buildFromDegree().getMatrix();

    // Spine order: Bottom to Top
    const spineOrder = [
      'S1', 'L5', 'L4', 'L3', 'L2', 'L1',
      'T12', 'T11', 'T10', 'T9', 'T8', 'T7', 'T6', 'T5', 'T4', 'T3', 'T2', 'T1',
      'C7', 'C6', 'C5', 'C4', 'C3', 'C2', 'C1'
    ];

    // Map to accumulate global transforms relative to S1 (fixed)
    // We start with identity for everyone
    const globalTransforms = {};

    // Calculate total correction angle for global counter-rotation
    let totalCorrectionDelta = 0;
    Object.values(corrections).forEach(val => {
      const num = parseFloat(val);
      if (!isNaN(num)) totalCorrectionDelta += num;
    });

    // Initialize base (S1) with counter-rotation to keep spine upright
    const s1Builder = vtkMatrixBuilder.buildFromDegree();
    if (totalCorrectionDelta !== 0 && vertebraBounds['S1']) {
      const s1Bounds = vertebraBounds['S1'];
      const s1Center = [
        (s1Bounds[0] + s1Bounds[1]) / 2,
        (s1Bounds[2] + s1Bounds[3]) / 2,
        (s1Bounds[4] + s1Bounds[5]) / 2
      ];

      console.log(`Applying global counter-rotation: ${-totalCorrectionDelta} deg`);
      // Rotate entire model back by total delta around S1 center
      s1Builder
        .translate(s1Center[0], s1Center[1], s1Center[2])
        .rotateX(-totalCorrectionDelta)
        .translate(-s1Center[0], -s1Center[1], -s1Center[2]);
    }

    // key is 'S1', value is builder
    globalTransforms['S1'] = s1Builder;

    // Initialize other levels with identity builders initially
    spineOrder.forEach(level => {
      if (level !== 'S1') {
        globalTransforms[level] = vtkMatrixBuilder.buildFromDegree();
      }
    });

    // Iterate from bottom up - this section was cut off in previous view, assuming it handles FK



    for (let i = 0; i < spineOrder.length; i++) {
      const currentLevel = spineOrder[i];

      // If this level has a correction "above" it (e.g. L4-L5 affects L4 and above)
      // Note: correction 'L4-L5' means rotation of L4 relative to L5.
      // So we look for correction where currentLevel is the TOP vertebra.

      // Find if there is a correction involving this level as the UPPER vertebra
      // e.g. if current is L4, we look for 'L4-L5'.
      // If found, we apply rotation to L4 and propagate to all superior levels.

      // Actually, we can just process the corrections. 
      // A correction 'L3-L4': 5deg means L3 rotates 5deg relative to L4.
      // L4 is the "base" for this rotation.

      // Let's iterate through the sorted spine and apply cumulative transforms.

      if (i === 0) continue; // S1 is fixed (or whatever the bottom most is)

      const levelBelow = spineOrder[i - 1];
      const pairKey = `${currentLevel}-${levelBelow}`; // e.g., "L5-S1", "L4-L5"

      const correctionDeg = corrections[pairKey] ? parseFloat(corrections[pairKey]) : 0;
      const distractionMm = distractions && distractions[pairKey] ? parseFloat(distractions[pairKey]) : 0;

      // Start with the transform of the level below
      const parentTransformMatrix = globalTransforms[levelBelow].getMatrix();
      const currentBuilder = vtkMatrixBuilder.buildFromDegree().setMatrix(parentTransformMatrix);

      if (correctionDeg !== 0 || distractionMm !== 0) {
        // Calculate Pivot Point (COR) - Inferior face of current vertebra
        const bounds = vertebraBounds[currentLevel];
        if (bounds) {
          const center = [
            (bounds[0] + bounds[1]) / 2,
            (bounds[2] + bounds[3]) / 2,
            bounds[4] // Bottom (Min Z)
          ];

          currentBuilder
            .translate(center[0], center[1], center[2]);

          // Apply Rotation (Extension/Flexion)
          if (correctionDeg !== 0) {
            currentBuilder.rotateX(correctionDeg);
          }

          // Apply Distraction (Vertical lift along Z)
          // Applied in local space of the joint
          if (distractionMm !== 0) {
            console.log(`[Kinematics] Applying distraction of ${distractionMm}mm at ${pairKey}`);
            currentBuilder.translate(0, 0, distractionMm);
          }

          currentBuilder
            .translate(-center[0], -center[1], -center[2]);
        }
      }

      globalTransforms[currentLevel] = currentBuilder;

      // If we have a transform different from identity, save it
      if (!currentBuilder.getMatrix().every((val, idx) => val === (idx % 5 === 0 ? 1 : 0))) {
        newTransforms[currentLevel] = currentBuilder.getMatrix();
      }
    }

    setCalculatedTransforms(newTransforms);

  }, [corrections, distractions, vertebraBounds]);

  // Handle model loaded callback
  const handleModelLoaded = useCallback((vertebraLabel) => {
    console.log(`Model loaded callback for ${vertebraLabel}`);
    setLoadedCount(prev => {
      const newCount = prev + 1;
      const currentVisibleModels = computedModels.filter(m => m.isVisible);
      const totalVisible = currentVisibleModels.length;
      console.log(`Loaded ${newCount}/${totalVisible} models`);

      if (totalVisible > 0) {
        const progress = (newCount / totalVisible) * 100;
        setLoadingProgress(progress);

        // Stop loading when all visible models are loaded
        if (newCount >= totalVisible) {
          console.log("All visible models loaded, stopping loading state");
          setIsLoading(false);
        }
      }
      return newCount;
    });
  }, [computedModels]);

  // Fit the whole model in view with breathing room. resetCamera() alone fits
  // the bounding sphere edge-to-edge; the zoom-out leaves a margin so the full
  // spine is comfortably visible at any panel size.
  const fitCameraToModels = useCallback(() => {
    const renderer = rendererRef.current;
    if (!renderer) return;
    renderer.resetCamera();
    renderer.getActiveCamera().zoom(0.9);
    if (renderWindowRef.current) {
      renderWindowRef.current.render();
    }
  }, []);

  // Re-fit whenever the host panel changes size — the canvas resizes with the
  // container, but the camera keeps the old framing and crops the model.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !vtkReady || typeof ResizeObserver === 'undefined') return;
    let resizeTimer;
    const observer = new ResizeObserver(() => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        try {
          fitCameraToModels();
        } catch (error) {
          console.warn('Error refitting camera on resize:', error);
        }
      }, 150);
    });
    observer.observe(container);
    return () => {
      clearTimeout(resizeTimer);
      observer.disconnect();
    };
  }, [vtkReady, fitCameraToModels]);

  // Reset camera when models change
  useEffect(() => {
    console.log("[DEBUG] SpineModelViewer Render. Props:", { showHardware, hasScrews: pedicleScrews && Object.keys(pedicleScrews).length > 0, screwsKeys: pedicleScrews ? Object.keys(pedicleScrews) : "null" });
    if (rendererRef.current && spineModels.length > 0 && !isLoading) {
      // Small delay to ensure models are loaded
      setTimeout(() => {
        try {
          console.log(`[Camera] Resetting camera for ${spineModels.length} models`);
          fitCameraToModels();
          console.log('[Camera] Camera reset and render completed');
        } catch (error) {
          console.warn('Error resetting camera:', error);
        }
      }, 500);
    }
  }, [spineModels, isLoading, fitCameraToModels]);

  // Reset camera when alignment/transforms change
  useEffect(() => {
    console.log("[DEBUG] SpineModelViewer Render. Props:", { showHardware, hasScrews: pedicleScrews && Object.keys(pedicleScrews).length > 0, screwsKeys: pedicleScrews ? Object.keys(pedicleScrews) : "null" });
    if (rendererRef.current && vtkReady && !isLoading) {
      // Only reset if this is the initial load phase (or we haven't reset for transforms yet)
      if (!hasInitialTransformReset.current) {
        // Debounce slightly to ensure VTK actors have updated their user matrices
        const timer = setTimeout(() => {
          try {
            console.log('[Camera] Initial reset for transforms');
            fitCameraToModels();
            hasInitialTransformReset.current = true; // Mark as done
          } catch (error) {
            console.warn('Error resetting camera on transform change:', error);
          }
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [calculatedTransforms, vtkReady, isLoading, fitCameraToModels]);

  // Only render when spine region visibility changes (not for individual selections)
  const prevSpineOptionsRef = useRef(spineOptions);
  useEffect(() => {
    console.log("[DEBUG] SpineModelViewer Render. Props:", { showHardware, hasScrews: pedicleScrews && Object.keys(pedicleScrews).length > 0, screwsKeys: pedicleScrews ? Object.keys(pedicleScrews) : "null" });
    if (renderWindowRef.current) {
      const spineOptionsChanged = JSON.stringify(prevSpineOptionsRef.current) !== JSON.stringify(spineOptions);
      if (spineOptionsChanged) {
        console.log("Spine region visibility changed, triggering render");
        prevSpineOptionsRef.current = spineOptions;
        setTimeout(() => {
          if (renderWindowRef.current) {
            renderWindowRef.current.render();
          }
        }, 50);
      }
    }
  }, [spineOptions]);

  return (
    <div
      ref={containerRef}
      className="viewer"
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        // Fill the host panel instead of forcing 400px — a floor taller than
        // the panel clips the canvas and crops the spine (Planning's cards).
        minHeight: '180px',
        backgroundColor: '#2a2a2a'
      }}
    >
      {isLoading && <LoadingIndicator progress={loadingProgress} />}
      {error && <ErrorDisplay error={error} />}

      {/* Debug info */}
      {!isLoading && !error && !rendererRef.current && (
        <div
          className="empty"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 6,
            width: 'max-content',
            maxWidth: 'calc(100% - 32px)',
            padding: 0
          }}
        >
          <div className="empty__title">VTK Renderer not initialized</div>
        </div>
      )}

      {/* Render vertebrae */}
      {rendererRef.current && computedModels.map(model => {
        // Only log for selected or concerning vertebrae to reduce console spam
        if (model.isSelected || model.isConcerning || model.isHerniated) {
          console.log(`[Render] VTKVertebra ${model.level}: visible=${model.isVisible}, selected=${model.isSelected}, concerning=${model.isConcerning}, herniated=${model.isHerniated}`);
        }
        return (
          <VTKVertebra
            key={model.filename}
            modelUrl={model.url}
            isSelected={model.isSelected}
            isConcerning={model.isConcerning}
            isHerniated={model.isHerniated}
            isVisible={model.isVisible}
            vertebraLabel={model.level}
            renderer={rendererRef.current}
            onLoaded={handleModelLoaded}
            onBoundsReady={handleBoundsReady}
            userMatrix={calculatedTransforms[model.level]}
          />
        );
      })}
    </div>
  );
};

// Main component (same interface as Three.js version)
function SpineModelViewer({
  selectedLevel,
  selectedLevels = [],
  concerningLevels = [],
  herniatedLevels = [],
  spineOptions,
  showInfoPanel = true,
  corrections, // Accept corrections prop
  distractions, // New prop for vertical height adjustments
  showHardware = false,
  pedicleScrews = {},
  interbodyDevices = {},
  vertebralMetrics, // New prop for precise measurements
  viewerId = "unknown"
}) {
  const allSelectedLevels = useMemo(() => {
    const combinedLevels = [...selectedLevels];
    if (selectedLevel && !combinedLevels.includes(selectedLevel)) {
      combinedLevels.push(selectedLevel);
    }
    return combinedLevels;
  }, [selectedLevel, selectedLevels]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <VTKSpineScene
        selectedLevel={selectedLevel}
        selectedLevels={selectedLevels}
        concerningLevels={concerningLevels}
        herniatedLevels={herniatedLevels}
        spineOptions={spineOptions}
        corrections={corrections} // Pass corrections down to scene
        distractions={distractions} // Pass distractions down to scene
        showHardware={showHardware}
        pedicleScrews={pedicleScrews}
        interbodyDevices={interbodyDevices}
        vertebralMetrics={vertebralMetrics}
        viewerId={viewerId}
      />

      {/* UI overlay - conditionally rendered based on showInfoPanel prop */}
      {showInfoPanel && (
        <div style={styles.modelInfo} className="viewer__hud">
          <div style={styles.modelInfoContent}>
            <span style={styles.modelLabel}>
              {allSelectedLevels.length > 0 ? (
                allSelectedLevels.length > 1 ? (
                  <>
                    Selected: <strong style={styles.modelHighlight}>{allSelectedLevels.length} vertebrae</strong>
                    <span style={styles.selectedChips}>
                      {allSelectedLevels.sort().map(level => (
                        <span key={level} className="badge badge--neutral" style={styles.levelChip}>{level}</span>
                      ))}
                    </span>
                  </>
                ) : (
                  <>
                    Selected: <span className="badge badge--neutral" style={styles.levelChip}><strong style={styles.modelHighlight}>{allSelectedLevels[0]}</strong></span>
                  </>
                )
              ) : (
                <>No vertebrae selected</>
              )}
            </span>
            {concerningLevels.length > 0 && (
              <span className="badge badge--danger" style={styles.legendBadge}>
                <span className="status-dot" style={{ background: 'var(--danger)' }}></span>
                {concerningLevels.length} concerning for stenosis
              </span>
            )}
            {herniatedLevels.length > 0 && (
              <span className="badge badge--warning" style={styles.legendBadge}>
                <span className="status-dot" style={{ background: 'var(--warning)' }}></span>
                {herniatedLevels.length} herniated
              </span>
            )}
            <span style={styles.modelRegions}>
              {spineOptions.cervical && <span className="badge badge--accent" style={styles.regionBadge}>Cervical</span>}
              {spineOptions.thoracic && <span className="badge badge--accent" style={styles.regionBadge}>Thoracic</span>}
              {spineOptions.lumbar && <span className="badge badge--accent" style={styles.regionBadge}>Lumbar</span>}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Styles (info-panel overlay; static values only — dynamic values stay inline)
const styles = {
  modelInfo: {
    position: 'absolute',
    bottom: '12px',
    left: '12px',
    maxWidth: 'min(380px, calc(100% - 24px))',
    padding: '10px 12px',
    fontSize: '0.78rem',
    color: 'var(--text-secondary)',
    pointerEvents: 'none',
  },
  modelInfoContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
  },
  modelLabel: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px',
    fontWeight: 500,
    letterSpacing: '0.01em',
  },
  modelHighlight: {
    color: getVertebraColor('selected', 'hex'),
    fontWeight: 600,
    fontFamily: 'var(--font-mono)',
  },
  selectedChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  levelChip: {
    height: '20px',
    padding: '0 8px',
    fontSize: '0.68rem',
    fontFamily: 'var(--font-mono)',
  },
  legendBadge: {
    height: '22px',
    fontSize: '0.7rem',
  },
  modelRegions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  regionBadge: {
    height: '20px',
    padding: '0 8px',
    fontSize: '0.66rem',
  }
};

export default SpineModelViewer;